// Leitor do MARCADOR do "Trabalhe Conosco em etapas" (contrato §2).
//
// O HTML das "Páginas do Site" entra no site por dangerouslySetInnerHTML, que NÃO executa
// <script>. O builder (imobtotal-web) grava então um bloco morto:
//
//   <div class="itp-tc-form" data-itp-block="trabalhe-conosco" data-tc="{&quot;v&quot;:1,…}">
//     <div class="itp-tc-fallback">…texto com o contato da imobiliária…</div>
//   </div>
//
// e o site troca esse bloco INTEIRO pelo componente React. Quando qualquer coisa aqui falha
// (sem marcador, JSON quebrado, versão desconhecida, token vazio = tenant sem o portão)
// devolvemos `null` e a página segue renderizando o HTML como sempre — o visitante vê o
// texto de fallback do próprio marcador.
//
// Espelho de `lib/avm/placeholder.ts` (calculadora), de propósito SEM importar dele: os dois
// leitores são independentes e a compilação isolada do teste (scripts/test-tc-placeholder.mjs)
// não precisa de nada além deste arquivo.
//
// ⚠️ Arquivo PURO (sem React, sem DOM, sem imports de runtime): roda no servidor, no
// navegador e no teste `node scripts/test-tc-placeholder.mjs`.

import type { TcCampo, TcConfig, TcEtapa, TcSplit, TcTheme, TcTipoCampo, TcValidacao } from './types';

/** Defaults do contrato §2 — tudo fora de `v`/`codsite`/`token` é opcional no JSON. */
export const TC_THEME_PADRAO: TcTheme = {
    primary: '#0b3d5c',
    secondary: '#c9a24b',
    bg: '#ffffff',
    text: '#1f2937',
    surface: '#f8fafc',
    muted: '#6b7280',
};

export const TC_TEXTOS_PADRAO = {
    titulo: 'Trabalhe conosco',
    subtitulo: 'Conte um pouco sobre você. Leva poucos minutos e você pode continuar depois de onde parou.',
    mensagemSucesso: 'Recebemos a sua candidatura! Nossa equipe vai analisar e entra em contato em breve.',
    consentText:
        'Autorizo o tratamento dos meus dados para fins de recrutamento e seleção, conforme a Política de Privacidade.',
};

/** Chaves da etapa 1 (fixa): não podem aparecer nas `etapas` do builder (contrato §1). */
export const TC_CHAVES_RESERVADAS = ['nome', 'email', 'celular', 'cidade', 'vagas'];

const TIPOS_CAMPO: TcTipoCampo[] = ['short', 'long', 'select', 'multi', 'single'];
const VALIDACOES: TcValidacao[] = ['none', 'email', 'telefone', 'cpf', 'cnpj', 'data'];

/** `<div … class="…itp-tc-form…" …>` — a ordem dos atributos do builder pode mudar. */
const RE_ABERTURA = /<div\b[^>]*\bclass\s*=\s*"[^"]*\bitp-tc-form\b[^"]*"[^>]*>/i;
const RE_DATA_TC = /\bdata-tc\s*=\s*"([^"]*)"/i;
/** varredura de <div …> e </div> a partir do fim da tag de abertura. */
const RE_TAG_DIV = /<div\b[^>]*>|<\/div\s*>/gi;

/**
 * Desfaz o escape de ATRIBUTO HTML que o builder aplica no JSON.
 * `&amp;` vem por ÚLTIMO de propósito: senão `&amp;quot;` (um `&quot;` literal no texto)
 * viraria uma aspa de verdade e quebraria o JSON.
 */
export function desescaparAtributoHtml(valor: string): string {
    return String(valor)
        .replace(/&quot;/g, '"')
        .replace(/&#0*34;/g, '"')
        .replace(/&#0*39;/g, "'")
        .replace(/&apos;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
}

function texto(valor: unknown, padrao: string): string {
    return typeof valor === 'string' && valor.trim() ? valor.trim() : padrao;
}

function booleano(valor: unknown, padrao: boolean): boolean {
    if (typeof valor === 'boolean') return valor;
    if (valor === 'true' || valor === 1 || valor === '1') return true;
    if (valor === 'false' || valor === 0 || valor === '0') return false;
    return padrao;
}

/**
 * Cor só entra no `style` do wrapper (custom properties) se for reconhecível.
 * Não é XSS (o React usa setProperty), mas lixo no CSS quebraria o visual em silêncio.
 */
function cor(valor: unknown, padrao: string): string {
    const v = typeof valor === 'string' ? valor.trim() : '';
    if (!v || v.length > 32) return padrao;
    if (/^#[0-9a-f]{3,8}$/i.test(v)) return v;
    if (/^rgba?\(\s*[\d.\s,%/]+\)$/i.test(v)) return v;
    if (/^hsla?\(\s*[\d.\s,%/deg]+\)$/i.test(v)) return v;
    if (/^[a-z]{3,20}$/i.test(v)) return v; // nomes CSS (white, black, …)
    return padrao;
}

function inteiroOuNulo(valor: unknown): number | null {
    const n = typeof valor === 'number' ? valor : parseInt(String(valor ?? ''), 10);
    return Number.isFinite(n) && Number.isInteger(n) ? n : null;
}

/** largura em % (10..100); qualquer outra coisa = linha inteira. */
function larguraOuNula(valor: unknown): number | null {
    const n = typeof valor === 'number' ? valor : parseFloat(String(valor ?? ''));
    if (!Number.isFinite(n) || n < 10 || n >= 100) return null;
    return Math.round(n);
}

function listaDeTextos(valor: unknown, maximo: number): string[] {
    if (!Array.isArray(valor)) return [];
    const vistos: Record<string, true> = {};
    const saida: string[] = [];
    for (const item of valor) {
        const v = typeof item === 'string' ? item.trim() : typeof item === 'number' ? String(item) : '';
        if (!v || vistos[v]) continue;
        vistos[v] = true;
        saida.push(v.slice(0, 200));
        if (saida.length >= maximo) break;
    }
    return saida;
}

/**
 * Normaliza UM campo do builder. Devolve `null` quando o campo não tem como ser exibido
 * (sem `key`, chave reservada da etapa 1, campo de opções sem opção, chave repetida).
 */
export function normalizarCampo(cru: unknown, vistos: Record<string, true>): TcCampo | null {
    if (!cru || typeof cru !== 'object' || Array.isArray(cru)) return null;
    const o = cru as Record<string, unknown>;

    const key = typeof o.key === 'string' ? o.key.trim().slice(0, 80) : '';
    if (!key || vistos[key] || TC_CHAVES_RESERVADAS.indexOf(key) !== -1) return null;

    const tipoCru = typeof o.type === 'string' ? o.type.trim() : '';
    const type: TcTipoCampo = TIPOS_CAMPO.indexOf(tipoCru as TcTipoCampo) !== -1 ? (tipoCru as TcTipoCampo) : 'short';

    const options = type === 'select' || type === 'single' || type === 'multi' ? listaDeTextos(o.options, 60) : [];
    if ((type === 'select' || type === 'single' || type === 'multi') && !options.length) return null;

    const validarCru = typeof o.validate === 'string' ? o.validate.trim() : '';
    const validate: TcValidacao =
        type === 'short' && VALIDACOES.indexOf(validarCru as TcValidacao) !== -1 ? (validarCru as TcValidacao) : 'none';

    vistos[key] = true;
    return {
        key,
        type,
        label: texto(o.label, key).slice(0, 200),
        required: booleano(o.required, false),
        width: larguraOuNula(o.width),
        validate,
        help: typeof o.help === 'string' ? o.help.trim().slice(0, 300) : '',
        options,
    };
}

/** Normaliza as etapas do builder: descarta as vazias (sem nenhum campo exibível). */
export function normalizarEtapas(cru: unknown): TcEtapa[] {
    if (!Array.isArray(cru)) return [];
    const vistos: Record<string, true> = {};
    const idsVistos: Record<string, true> = {};
    const etapas: TcEtapa[] = [];
    for (let i = 0; i < cru.length && etapas.length < 20; i += 1) {
        const item = cru[i];
        if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
        const o = item as Record<string, unknown>;
        const campos: TcCampo[] = [];
        if (Array.isArray(o.campos)) {
            for (const c of o.campos) {
                const campo = normalizarCampo(c, vistos);
                if (campo) campos.push(campo);
                if (campos.length >= 30) break;
            }
        }
        if (!campos.length) continue;
        let id = typeof o.id === 'string' && o.id.trim() ? o.id.trim().slice(0, 80) : `etapa-${i + 1}`;
        if (idsVistos[id]) id = `${id}-${i + 1}`;
        idsVistos[id] = true;
        etapas.push({
            id,
            titulo: texto(o.titulo, `Etapa ${etapas.length + 1}`).slice(0, 200),
            ajuda: typeof o.ajuda === 'string' ? o.ajuda.trim().slice(0, 500) : '',
            campos,
        });
    }
    return etapas;
}

/** Aplica os defaults do contrato sobre o JSON cru. Devolve `null` se o mínimo não vier. */
export function normalizarTcConfig(cru: unknown): TcConfig | null {
    if (!cru || typeof cru !== 'object' || Array.isArray(cru)) return null;
    const o = cru as Record<string, unknown>;

    if (Number(o.v) !== 1) return null;

    const codsite = inteiroOuNulo(o.codsite);
    if (!codsite || codsite <= 0) return null;

    // token vazio = tenant sem o portão do Trabalhe Conosco (o builder serializa `token: ""`).
    // Nesse caso o site mostra o FALLBACK do marcador, não as etapas.
    if (typeof o.token !== 'string' || !o.token.trim()) return null;

    const temaCru = (o.theme && typeof o.theme === 'object' ? o.theme : {}) as Record<string, unknown>;
    const curriculoCru = (o.curriculo && typeof o.curriculo === 'object' ? o.curriculo : {}) as Record<string, unknown>;
    const curriculoAtivo = booleano(curriculoCru.ativo, true);

    return {
        v: 1,
        codsite,
        token: o.token.trim(),
        pageId: inteiroOuNulo(o.pageId),
        blockId: typeof o.blockId === 'string' && o.blockId.trim() ? o.blockId.trim() : null,
        titulo: texto(o.titulo, TC_TEXTOS_PADRAO.titulo),
        subtitulo: texto(o.subtitulo, TC_TEXTOS_PADRAO.subtitulo),
        mensagemSucesso: texto(o.mensagemSucesso, TC_TEXTOS_PADRAO.mensagemSucesso),
        consentText: texto(o.consentText, TC_TEXTOS_PADRAO.consentText),
        curriculo: {
            ativo: curriculoAtivo,
            // currículo desligado nunca é obrigatório
            obrigatorio: curriculoAtivo && booleano(curriculoCru.obrigatorio, true),
        },
        mostrarVagas: booleano(o.mostrarVagas, true),
        etapas: normalizarEtapas(o.etapas),
        theme: {
            primary: cor(temaCru.primary, TC_THEME_PADRAO.primary),
            secondary: cor(temaCru.secondary, TC_THEME_PADRAO.secondary),
            bg: cor(temaCru.bg, TC_THEME_PADRAO.bg),
            text: cor(temaCru.text, TC_THEME_PADRAO.text),
            surface: cor(temaCru.surface, TC_THEME_PADRAO.surface),
            muted: cor(temaCru.muted, TC_THEME_PADRAO.muted),
        },
    };
}

/**
 * Acha o PRIMEIRO marcador e devolve o HTML de antes, a config, o fallback interno e o HTML
 * de depois. Nada encontrado / inválido → `null` (a página cai no comportamento de hoje).
 */
export function extrairTcPlaceholder(html: unknown): TcSplit | null {
    if (typeof html !== 'string' || html.indexOf('itp-tc-form') === -1) return null;

    const abertura = RE_ABERTURA.exec(html);
    if (!abertura || abertura.index == null) return null;

    const tagAbertura = abertura[0];
    const dataTc = RE_DATA_TC.exec(tagAbertura);
    if (!dataTc) return null;

    let cru: unknown;
    try {
        cru = JSON.parse(desescaparAtributoHtml(dataTc[1]));
    } catch {
        return null;
    }

    const config = normalizarTcConfig(cru);
    if (!config) return null;

    // Fim do bloco: conta <div>/</div> a partir do fim da tag de abertura. O contrato garante
    // 1 div interno (o fallback), mas contar também aguenta 0 ou espaços entre os fechamentos.
    const inicioConteudo = abertura.index + tagAbertura.length;
    RE_TAG_DIV.lastIndex = inicioConteudo;
    let profundidade = 1;
    let fim = -1;
    let fimConteudo = -1;
    let tag: RegExpExecArray | null;
    while ((tag = RE_TAG_DIV.exec(html)) !== null) {
        if (tag[0].charAt(1) === '/') {
            profundidade -= 1;
            if (profundidade === 0) {
                fimConteudo = tag.index;
                fim = tag.index + tag[0].length;
                break;
            }
        } else {
            profundidade += 1;
        }
    }
    RE_TAG_DIV.lastIndex = 0;
    if (fim === -1) return null; // marcador não fechado: não mexe no HTML

    return {
        antes: html.slice(0, abertura.index),
        config,
        fallback: html.slice(inicioConteudo, fimConteudo),
        depois: html.slice(fim),
    };
}
